import { isYakLoggingEnabled, logger } from "./logger.js";
import { debounce, extractPageContext } from "./page-context.js";
import type { ChatConfig } from "./types/config.js";
import type {
  IframeMessageFromHost,
  IframeMessageToHost,
  Theme,
  UserIdentity,
} from "./types/messaging.js";
import type { ToolCallEventListener, ToolCallHandler } from "./types/tools.js";
import { EMBED_PROTOCOL_VERSION } from "./version.js";

/** localStorage key for the per-app signed session token. */
const SESSION_STORAGE_KEY = (appId: string) => `yak:session:${appId}`;
/** localStorage key for the per-app active-conversation pointer. */
const CONVERSATION_POINTER_KEY = (appId: string) => `yak:conversation:${appId}`;

/** Read a stored session token for this app, if any. SSR-safe. */
function readStoredSessionToken(appId: string): string | undefined {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return undefined;
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY(appId)) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStoredSessionToken(appId: string, token: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY(appId), token);
  } catch {
    // localStorage can throw in private-browsing modes; silently ignore.
  }
}

function clearStoredSessionToken(appId: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY(appId));
  } catch {
    // localStorage can throw in private-browsing modes; silently ignore.
  }
}

function readStoredConversationPointer(appId: string): string | undefined {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return undefined;
  try {
    return window.localStorage.getItem(CONVERSATION_POINTER_KEY(appId)) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStoredConversationPointer(appId: string, pointer: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
  try {
    window.localStorage.setItem(CONVERSATION_POINTER_KEY(appId), pointer);
  } catch {
    // localStorage can throw in private-browsing modes; silently ignore.
  }
}

function clearStoredConversationPointer(appId: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
  try {
    window.localStorage.removeItem(CONVERSATION_POINTER_KEY(appId));
  } catch {
    // localStorage can throw in private-browsing modes; silently ignore.
  }
}

/** Production chat origin — where the widget iframe loads from by default. */
const DEFAULT_CHAT_ORIGIN = "https://chat.yak.io";

// Local development flag, set by yak's own apps to point the widget at a chat
// UI running on localhost. `__YAK_LOGGING_ENABLED__` toggles verbose SDK logs.
declare global {
  interface Window {
    __YAK_INTERNAL_DEV__?: boolean;
    __YAK_LOGGING_ENABLED__?: boolean;
  }
}

/**
 * Resolves the iframe origin when no explicit `origin` is configured. Points at
 * a local chat UI during yak's own local development; production otherwise.
 * Integrators override the result with the `origin` config option.
 */
function getDefaultIframeOrigin(): string {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
    typeof window.__YAK_INTERNAL_DEV__ !== "undefined"
  ) {
    return "http://localhost:3001";
  }
  return DEFAULT_CHAT_ORIGIN;
}

export interface YakClientConfig {
  appId: string;
  /**
   * Override the origin the chat widget iframe loads from. Defaults to
   * `https://chat.yak.io`. Most integrators never set this — it exists for
   * non-production environments (e.g. a chat UI running on localhost).
   */
  origin?: string;
  /**
   * Handler for tool calls from the chat widget.
   * The consuming platform decides how to execute (browser, server fetch, etc.)
   *
   * @example Browser-only execution
   * ```ts
   * onToolCall: async (name, args) => {
   *   if (name === "ui.scrollTo") {
   *     document.getElementById(args.id)?.scrollIntoView();
   *     return { success: true };
   *   }
   *   throw new Error(`Unknown tool: ${name}`);
   * }
   * ```
   *
   * @example Server delegation
   * ```ts
   * onToolCall: async (name, args) => {
   *   const res = await fetch("/api/yak/tools", {
   *     method: "POST",
   *     body: JSON.stringify({ name, args }),
   *   });
   *   const data = await res.json();
   *   if (!data.ok) throw new Error(data.error);
   *   return data.result;
   * }
   * ```
   */
  onToolCall?: ToolCallHandler;
  theme?: Theme;
  chatConfig?: ChatConfig;
  onRedirect?: (path: string) => void;
  onClose?: () => void;
  onReady?: () => void;
  /**
   * Called after every tool call completes (success or failure).
   * Useful for page-level cache invalidation based on which tools were called.
   *
   * @example
   * ```ts
   * onToolCallComplete: (event) => {
   *   if (event.ok && event.name.startsWith("order.")) {
   *     queryClient.invalidateQueries({ queryKey: ["orders"] });
   *   }
   * }
   * ```
   */
  onToolCallComplete?: ToolCallEventListener;
  /** Chat configuration options */
  options?: {
    /** Disable the restart session button in the header */
    disableRestartButton?: boolean;
    /**
     * Stop sending any page context (URL, title, visible text) to the assistant.
     * The widget still works; it just won't be aware of the current page, so it
     * can't answer questions like "what is this page?". Use this when page content
     * must not leave the customer's site for privacy reasons.
     */
    disablePageContent?: boolean;
  };
  /**
   * Signed end-user identity. When supplied, the widget persists conversations
   * server-side keyed to this user and surfaces a history pane. The `hash`
   * must be HMAC-SHA256(apiSecret, id) computed on the integrator's backend —
   * never expose `apiSecret` to the browser.
   *
   * @example
   * ```ts
   * // Integrator backend (Node.js)
   * const hash = crypto
   *   .createHmac("sha256", process.env.YAK_API_SECRET)
   *   .update(currentUser.id)
   *   .digest("hex");
   *
   * // Browser
   * new YakClient({
   *   appId: "app_abc",
   *   user: { id: currentUser.id, hash },
   * });
   * ```
   */
  user?: UserIdentity;
}

export class YakClient {
  private config: YakClientConfig;
  private iframeWindow: Window | null = null;
  private isWidgetOpen = false;
  private readyTarget: { window: Window; origin: string } | null = null;
  private unexpectedOriginLogged = false;
  private lastUrl = " ";
  private debouncedSendContext: () => void;
  private observer: MutationObserver | null = null;

  constructor(config: YakClientConfig) {
    this.config = config;
    this.debouncedSendContext = debounce(() => {
      logger.debug("DOM mutation detected, sending page context");
      this.sendPageContext();
    }, 2000);
  }

  public updateConfig(newConfig: Partial<YakClientConfig>) {
    // Proactively rotate the session when the host swaps end-users, so a token
    // claimed by one user is never reused under another. The server enforces
    // this too (a bound token is rejected for a different identity), but
    // clearing here means the common case never hits that rejection.
    //
    // Only act when `newConfig` actually carries a `user` key — many callers
    // pass unrelated partials (e.g. `{ chatConfig }`) that must not disturb the
    // token. Clear on a change to a *different* identity or a logout; never on
    // the anonymous→identified first login, which must keep the session so the
    // server can claim the visitor's pre-login history.
    if ("user" in newConfig) {
      const previousUserId = this.config.user?.id;
      const nextUserId = newConfig.user?.id;
      const changedToDifferentUser = previousUserId && previousUserId !== nextUserId;
      if (changedToDifferentUser) {
        clearStoredSessionToken(this.config.appId);
        clearStoredConversationPointer(this.config.appId);
      }
    }

    this.config = { ...this.config, ...newConfig };
    // Resend config when the iframe is ready and we have anything to deliver —
    // tool/route manifests, or a user identity that needs to land in the
    // widget so persistence/history light up.
    if (this.readyTarget && (this.config.chatConfig || this.config.user)) {
      this.sendConfigToIframe(this.readyTarget.window, this.readyTarget.origin);
    }
  }

  /**
   * Get the iframe origin URL (base URL for the chat widget). Recomputed on
   * each call so environment-dependent defaults resolve correctly.
   */
  public getIframeOrigin(): string {
    return this.config.origin ?? getDefaultIframeOrigin();
  }

  /**
   * Get the full iframe embed URL for the chatbot
   *
   * @example
   * ```ts
   * const client = new YakClient({ appId: "my-app" });
   * const iframeSrc = client.getEmbedUrl();
   * // Returns: "https://chat.yak.io/embed/v1/my-app"
   * ```
   */
  public getEmbedUrl(): string {
    const origin = this.getIframeOrigin();
    const baseUrl = `${origin}/embed/v${EMBED_PROTOCOL_VERSION}/${encodeURIComponent(this.config.appId)}`;

    const params = new URLSearchParams();
    const theme = this.config.theme;

    if (theme?.colorMode && theme.colorMode !== "system") {
      params.set("colorMode", theme.colorMode);
    }

    this.appendThemeColors(params, theme?.light, "light");
    this.appendThemeColors(params, theme?.dark, "dark");

    // Pass debug flag to iframe so it can enable logging immediately
    if (isYakLoggingEnabled()) {
      params.set("yakDebug", "1");
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  /**
   * Append theme color parameters to a URLSearchParams object
   */
  private appendThemeColors(
    params: URLSearchParams,
    colors: Theme["light"] | Theme["dark"] | undefined,
    prefix: "light" | "dark"
  ): void {
    if (!colors) return;
    const map: [string, string | undefined][] = [
      [`${prefix}Bg`, colors.background],
      [`${prefix}Border`, colors.border],
      [`${prefix}MessageBg`, colors.messageBackground],
      [`${prefix}Placeholder`, colors.placeholderColor],
      [`${prefix}SubmitBtn`, colors.submitButtonColor],
      [`${prefix}SubmitBtnText`, colors.submitButtonTextColor],
      [`${prefix}HeaderIcon`, colors.headerIconColor],
    ];
    for (const [key, value] of map) {
      if (value) params.set(key, value);
    }
  }

  /**
   * Get the app ID
   */
  public getAppId(): string {
    return this.config.appId;
  }

  /**
   * Get the current theme configuration
   */
  public getTheme(): Theme | undefined {
    return this.config.theme;
  }

  /**
   * Send a prompt message to the chatbot iframe
   * Note: The iframe must be ready to receive messages (onReady callback must have fired)
   *
   * @example
   * ```ts
   * const client = new YakClient({ appId: "my-app", onReady: () => {
   *   client.sendPrompt("Help me with my order");
   * }});
   * ```
   */
  public sendPrompt(prompt: string): void {
    const target = this.getActiveTarget();
    if (!target) {
      logger.warn("Cannot send prompt: iframe not ready");
      return;
    }

    const message: IframeMessageFromHost = {
      type: "yak:prompt",
      payload: { prompt },
    };
    target.window.postMessage(message, target.origin);
  }

  /**
   * Send a focus request to the chatbot iframe
   * This will focus the chat input field
   */
  public sendFocus(): void {
    const target = this.getActiveTarget();
    if (!target) {
      logger.warn("Cannot send focus: iframe not ready");
      return;
    }

    const message: IframeMessageFromHost = {
      type: "yak:focus",
    };
    target.window.postMessage(message, target.origin);
  }

  /**
   * The live postMessage target. Prefers `readyTarget` — the window that
   * completed the `yak:ready` handshake — and falls back to the iframe's
   * `contentWindow`. The two are normally identical, but they're populated by
   * different events (the ready postMessage vs. the DOM `load` event) and can
   * briefly diverge (e.g. a remount under React StrictMode, where `isReady`
   * goes true before — or without — the load event repopulating
   * `iframeWindow`). Trusting `readyTarget` keeps `sendFocus`/`sendPrompt`
   * consistent with `isReady()` and avoids spurious "iframe not ready" warns.
   */
  private getActiveTarget(): { window: Window; origin: string } | null {
    if (this.readyTarget) return this.readyTarget;
    if (this.iframeWindow) return { window: this.iframeWindow, origin: this.getIframeOrigin() };
    return null;
  }

  /**
   * Check if the iframe is ready to receive messages
   */
  public isReady(): boolean {
    return this.readyTarget !== null;
  }

  public setIframeWindow(window: Window | null) {
    this.iframeWindow = window;
    if (!window) {
      this.readyTarget = null;
    }
  }

  public setWidgetOpen(isOpen: boolean) {
    this.isWidgetOpen = isOpen;
    if (isOpen && this.readyTarget && this.config.chatConfig) {
      this.sendConfigToIframe(this.readyTarget.window, this.readyTarget.origin);
    }

    if (isOpen) {
      this.startObserving();
    } else {
      this.stopObserving();
    }
  }

  public mount() {
    if (typeof window !== "undefined") {
      window.addEventListener("message", this.handleMessage);
      window.addEventListener("popstate", this.handlePopState);
    }
  }

  public unmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("message", this.handleMessage);
      window.removeEventListener("popstate", this.handlePopState);
    }
    this.stopObserving();
  }

  private startObserving() {
    // Gate on the same resolved target as sendPageContext — the observer resends
    // context on DOM/URL changes, so it's pointless without a target, but it must
    // not bail just because `iframeWindow` briefly trails `readyTarget`.
    if (typeof window === "undefined" || !this.getActiveTarget()) return;
    // No page context is sent when disabled, so there's nothing to observe for.
    if (this.config.options?.disablePageContent) return;

    // Initial check
    const currentUrl = window.location.href;
    if (currentUrl !== this.lastUrl) {
      this.lastUrl = currentUrl;
      logger.debug("URL changed, sending page context");
      this.sendPageContext();
    }

    if (this.observer) return;

    this.observer = new MutationObserver((mutations) => {
      const hasSubstantialChanges = mutations.some(
        (mutation) =>
          mutation.type === "childList" &&
          (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
      );

      if (hasSubstantialChanges) {
        this.debouncedSendContext();
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private handlePopState = () => {
    logger.debug("Navigation detected, sending page context");
    this.sendPageContext();
  };

  private handleMessage = (event: MessageEvent) => {
    if (typeof window === "undefined") return;
    if (!this.isWidgetOpen && event.data?.type !== "yak:ready") {
      return;
    }

    const hostOrigin = window.location.origin;
    const allowedOrigins = new Set<string>();
    allowedOrigins.add(this.getIframeOrigin());
    if (hostOrigin) {
      allowedOrigins.add(hostOrigin);
    }

    if (!allowedOrigins.has(event.origin)) {
      if (!this.unexpectedOriginLogged) {
        logger.warn(
          `Ignoring message from unexpected origin: ${event.origin}, allowed: ${Array.from(allowedOrigins).join(", ")}`
        );
        this.unexpectedOriginLogged = true;
      }
      return;
    }

    // Validate message structure
    if (!event.data || typeof event.data !== "object" || !("type" in event.data)) {
      // Filter out known browser extension messages
      const data = event.data as Record<string, unknown>;
      const isReactDevTools =
        data?.source === "react-devtools-content-script" ||
        data?.source === "react-devtools-bridge" ||
        data?.source === "react-devtools-inject-backend";
      const isReduxDevTools = data?.source === "@devtools-page";

      if (isReactDevTools || isReduxDevTools) {
        return;
      }
      return;
    }

    const message = event.data as IframeMessageToHost;
    const targetWindow =
      (event.source && "postMessage" in event.source ? (event.source as Window) : null) ??
      this.iframeWindow;
    const targetOrigin = this.getIframeOrigin();

    logger.debug("Message received from iframe:", message.type);

    switch (message.type) {
      case "yak:ready": {
        logger.debug("Iframe ready, sending config");

        if (targetWindow) {
          this.readyTarget = { window: targetWindow, origin: targetOrigin };
          this.sendConfigToIframe(targetWindow, targetOrigin);

          // Send initial page context after config
          setTimeout(() => this.sendPageContext(), 100);

          // Mark iframe as ready after config is sent
          setTimeout(() => this.config.onReady?.(), 200);
        } else {
          logger.warn("Unable to send config: iframe window not registered yet");
        }
        break;
      }

      case "yak:tool_call": {
        const { id, name, args } = message.payload;
        void this.handleToolCall(id, name, args);
        break;
      }

      case "yak:redirect": {
        const { path } = message.payload;
        logger.debug("Redirect request received:", path);

        if (!this.isAllowedRedirect(path)) {
          logger.warn("Blocked potentially unsafe redirect:", path);
          break;
        }

        if (this.config.onRedirect) {
          this.config.onRedirect(path);
        } else if (typeof window !== "undefined") {
          window.location.assign(path);
        }
        break;
      }

      case "yak:session": {
        const { sessionToken } = message.payload;
        logger.debug("Session token received from iframe; persisting");
        writeStoredSessionToken(this.config.appId, sessionToken);
        break;
      }

      case "yak:conversation": {
        const { pointer } = message.payload;
        if (pointer === null) {
          logger.debug("Conversation pointer cleared by iframe");
          clearStoredConversationPointer(this.config.appId);
        } else {
          logger.debug("Conversation pointer received from iframe; persisting");
          writeStoredConversationPointer(this.config.appId, pointer);
        }
        break;
      }

      case "yak:close": {
        logger.debug("Close message received from iframe");
        this.config.onClose?.();
        break;
      }

      default:
        logger.debug("Unknown message type:", (message as { type: string }).type);
        break;
    }
  };

  private sendConfigToIframe(targetWindow: Window, targetOrigin: string) {
    // Get logging enabled state from window flag
    const loggingEnabled =
      typeof window !== "undefined" && typeof window.__YAK_LOGGING_ENABLED__ === "boolean"
        ? window.__YAK_LOGGING_ENABLED__
        : undefined;

    const storedSessionToken = readStoredSessionToken(this.config.appId);
    const storedConversationPointer = readStoredConversationPointer(this.config.appId);

    const configMessage: IframeMessageFromHost = {
      type: "yak:config",
      payload: {
        version: EMBED_PROTOCOL_VERSION,
        appId: this.config.appId,
        theme: this.config.theme,
        toolManifest: this.config.chatConfig?.tools ?? undefined,
        routeManifest: this.config.chatConfig?.routes ?? undefined,
        options: this.config.options,
        loggingEnabled,
        user: this.config.user,
        sessionToken: storedSessionToken,
        conversationPointer: storedConversationPointer,
      },
    };

    logger.debug("Posting config to iframe origin:", {
      origin: targetOrigin,
      version: EMBED_PROTOCOL_VERSION,
      hasToolManifest: Boolean(this.config.chatConfig?.tools),
      toolCount: this.config.chatConfig?.tools?.tools.length ?? 0,
      hasRouteManifest: Boolean(this.config.chatConfig?.routes),
    });

    targetWindow.postMessage(configMessage, targetOrigin);
  }

  private sendPageContext() {
    // Resolve the live target the way sendFocus/sendPrompt do. Relying on
    // `iframeWindow` alone raced the initial send: it's populated by the iframe's
    // DOM `load` event, which can lag the `yak:ready` postMessage (or not refire
    // on a StrictMode remount), so the 100ms-after-ready send sometimes found it
    // null and silently dropped the page context — while config, sent via the
    // ready target, still arrived. `readyTarget` is set the instant ready lands.
    const target = this.getActiveTarget();
    if (!target) return;
    // Privacy opt-out: never read or transmit page context for this app.
    if (this.config.options?.disablePageContent) return;

    try {
      const pageContext = extractPageContext();
      const message: IframeMessageFromHost = {
        type: "yak:page_context",
        payload: pageContext,
      };
      logger.debug("Sending page context to iframe:", {
        url: pageContext.url,
        title: pageContext.title,
        textLength: pageContext.text.length,
      });
      target.window.postMessage(message, target.origin);
    } catch (error) {
      logger.error("Error extracting page context:", error);
    }
  }

  private async handleToolCall(id: string, name: string, args: unknown): Promise<void> {
    logger.debug(`Tool call received: ${name}`, { id, args });

    if (!this.config.onToolCall) {
      logger.error("Tool call received but no onToolCall handler configured");
      this.sendToolResultToIframe(id, false, undefined, "No tool call handler configured");
      this.config.onToolCallComplete?.({
        name,
        args,
        ok: false,
        error: "No tool call handler configured",
      });
      return;
    }

    try {
      const result = await this.config.onToolCall(name, args);
      logger.debug(`Tool call succeeded: ${name}`, { id });
      this.sendToolResultToIframe(id, true, result);
      this.config.onToolCallComplete?.({ name, args, ok: true, result });
    } catch (error) {
      const errorMessage = this.extractErrorMessage(error);
      // A thrown tool error is the documented way to surface a failure to the
      // model (see the adapter `execute` contract) — it's captured, returned to
      // the widget, and reported to the integrator via `onToolCallComplete`.
      // That makes it expected data flow, not a console-worthy fault, so log it
      // at debug to avoid noise in the host page's console.
      logger.debug(`Tool call failed: ${name}`, { id, error });
      this.sendToolResultToIframe(id, false, undefined, errorMessage);
      this.config.onToolCallComplete?.({ name, args, ok: false, error: errorMessage });
    }
  }

  private sendToolResultToIframe(id: string, ok: true, result: unknown): void;
  private sendToolResultToIframe(id: string, ok: false, result: undefined, error: string): void;
  private sendToolResultToIframe(id: string, ok: boolean, result: unknown, error?: string): void {
    if (!this.iframeWindow) return;

    if (ok) {
      // Serialize result to remove non-cloneable values (functions, etc.)
      // postMessage uses structured clone which cannot handle functions
      const safeResult = this.toSerializable(result);
      const message: IframeMessageFromHost = {
        type: "yak:tool_result",
        payload: { id, ok: true, result: safeResult },
      };
      this.iframeWindow.postMessage(message, this.getIframeOrigin());
    } else {
      const message: IframeMessageFromHost = {
        type: "yak:tool_result",
        payload: { id, ok: false, error: error ?? "Unknown error" },
      };
      this.iframeWindow.postMessage(message, this.getIframeOrigin());
    }
  }

  /**
   * Convert a value to a serializable form by stripping functions and other non-cloneable values.
   * Uses JSON.parse(JSON.stringify()) which handles most cases.
   */
  private toSerializable(value: unknown): unknown {
    if (value === undefined || value === null) {
      return value;
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      // If JSON serialization fails, return a string representation
      logger.warn("Failed to serialize tool result, returning string representation");
      return String(value);
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    return "Unknown error";
  }

  /**
   * Validates that a redirect path is safe (relative path or same-origin).
   * Blocks absolute URLs to external domains to prevent open redirect attacks.
   */
  private isAllowedRedirect(path: string): boolean {
    // Allow relative paths that don't start with // (protocol-relative URLs)
    if (path.startsWith("/") && !path.startsWith("//")) {
      return true;
    }
    // Allow hash-only and query-only paths
    if (path.startsWith("#") || path.startsWith("?")) {
      return true;
    }
    // For absolute URLs, verify same origin
    if (typeof window !== "undefined") {
      try {
        const url = new URL(path, window.location.origin);
        return url.origin === window.location.origin;
      } catch {
        // Invalid URL - block it
        return false;
      }
    }
    // In non-browser environments, only allow relative paths
    return false;
  }
}
